import { baseThemeValues } from 'constants/common';

import { buttonTheme } from '../../Button/theme';
import { scrollbarTheme } from '../../Scrollbar/theme';

const baseProps = {
  borderRadius: '5px',
  borderWidth: '1px',
  fontFamily: baseThemeValues.inherit,
  fontWeight: 400,
  outlineColor: 'palette.transparent',
  outlineWidth: '0px',
};

const placeholderBase = {
  fontFamily: baseThemeValues.inherit,
  fontWeight: 400,
};

const optionsBase = {
  fontFamily: baseThemeValues.inherit,
  fontWeight: 400,
};

const labelBase = {
  fontFamily: baseThemeValues.inherit,
  fontWeight: 500,
};

// ==================================================

const baseLgProps = {
  ...baseProps,
  fontSize: '16px',
};

const placeholderLgBase = {
  ...placeholderBase,
  fontSize: '16px',
};

const optionsLgBase = {
  ...optionsBase,
  fontSize: '16px',
};

const labelLgBase = {
  ...labelBase,
  fontSize: '14px',
};

// ==================================================

const baseSmProps = {
  ...baseProps,
  fontSize: '14px',
};

const placeholderSmBase = {
  ...placeholderBase,
  fontSize: '14px',
};

const optionsSmBase = {
  ...optionsBase,
  fontSize: '14px',
};

const labelSmBase = {
  ...labelBase,
  fontSize: '12px',
};

const clearButtonTheme = {
  active: {
    bg: 'palette.error.60',
    icon: 'palette.surface.3',
  },
  base: {
    bg: 'palette.error.60',
    icon: 'palette.surface.3',
  },
  hover: {
    bg: 'palette.error.70',
    icon: 'palette.surface.3',
  },
};

export const autocompleteSelectTheme = {
  lg: {
    baseProps: baseLgProps,
    clearButton: clearButtonTheme,
    disabled: {
      ...baseLgProps,
      bg: 'palette.neutral.10',
      borderColor: 'palette.neutral.10',
      label: {
        ...labelLgBase,
        text: 'palette.neutral.40',
      },
      placeholder: {
        ...placeholderLgBase,
        text: 'palette.neutral.40',
      },
      text: 'palette.neutral.40',
    },
    error: {
      ...baseLgProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.error.60',
      helpText: {
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 400,
        text: 'palette.error.60',
      },
      label: {
        ...labelLgBase,
        text: 'palette.error.60',
      },
      placeholder: {
        ...placeholderLgBase,
        text: 'palette.error.60',
      },
      text: 'palette.tertiary.95',
    },
    focus: {
      ...baseLgProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.tertiary.50',
      label: {
        ...labelLgBase,
        text: 'palette.tertiary.60',
      },
      outlineColor: 'palette.tertiary.50',
      outlineWidth: '1px',
      placeholder: {
        ...placeholderLgBase,
        text: 'palette.transparent',
      },
      text: 'palette.tertiary.95',
    },
    highlightedSearch: 'palette.primary.50:0.5',
    hover: {
      ...baseLgProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.tertiary.50',
      label: {
        ...labelLgBase,
        text: 'palette.tertiary.60',
      },
      placeholder: { ...placeholderLgBase, text: 'palette.tertiary.60' },
      text: 'palette.tertiary.95',
    },
    inactive: {
      ...baseLgProps,
      bg: 'palette.surface.4',
      borderColor: 'palette.tertiary.20',
      label: {
        ...labelLgBase,
        text: 'palette.tertiary.60',
      },
      placeholder: { ...placeholderLgBase, text: 'palette.tertiary.40' },
      text: 'palette.tertiary.95',
    },
    labelBase: labelLgBase,
    loadingText: {
      fontFamily: baseThemeValues.inherit,
      fontSize: '20px',
      fontWeight: 500,
      text: 'palette.primary.99',
    },
    multiTags: {
      tag: {
        bg: 'palette.transparent',
        borderColor: 'palette.neutral.10',
        borderRadius: '12px',
        borderWidth: '1px',
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 500,
        removeButton: 'palette.primary.99',
        text: 'palette.primary.99',
      },
      totalTag: {
        bg: 'palette.primary.20',
        borderColor: 'palette.transparent',
        borderRadius: '12px',
        borderWidth: '0px',
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 500,
        text: 'palette.primary.99',
      },
    },
    openArrowButton: buttonTheme.icon,
    openArrowColor: 'palette.tertiary.40',
    options: {
      active: {
        ...optionsLgBase,
        bg: 'palette.tertiary.20',
        text: 'palette.tertiary.70',
      },
      base: {
        ...optionsLgBase,
        bg: 'palette.transparent',
        text: 'palette.tertiary.95',
      },
      disabled: {
        ...optionsLgBase,
        bg: 'palette.neutral.5',
        text: 'palette.neutral.40',
      },
      hover: {
        ...optionsLgBase,
        bg: 'palette.tertiary.10',
        text: 'palette.tertiary.95',
      },
      optionsBase: optionsLgBase,
      selected: {
        ...optionsLgBase,
        bg: 'palette.transparent',
        text: 'palette.tertiary.95',
      },
    },
    optionsList: {
      bg: 'palette.surface.4',
      borderColor: 'palette.transparent',
      borderRadius: '5px',
      borderWidth: '0px',
      boxShadowColor: 'palette.black:0.5',
      divider: {
        bg: 'palette.transparent',
        height: '1px',
        verticalGap: '0px',
      },
      horizontalGap: '0px',
      verticalGap: '0px',
    },
    placeholderBase: placeholderLgBase,
    scrollBar: {
      ...scrollbarTheme,
      borderWidth: '3px',
      thumb: 'palette.tertiary.20',
      width: '9px',
    },
    warning: {
      label: {
        ...labelLgBase,
        text: 'palette.warning.40',
      },
    },
  },
  md: {
    baseProps: baseLgProps,
    clearButton: clearButtonTheme,
    disabled: {
      ...baseLgProps,
      bg: 'palette.neutral.10',
      borderColor: 'palette.neutral.10',
      label: {
        ...labelLgBase,
        text: 'palette.neutral.40',
      },
      placeholder: {
        ...placeholderLgBase,
        text: 'palette.neutral.40',
      },
      text: 'palette.neutral.40',
    },
    error: {
      ...baseLgProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.error.60',
      helpText: {
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 400,
        text: 'palette.error.60',
      },
      label: {
        ...labelLgBase,
        text: 'palette.error.60',
      },
      placeholder: {
        ...placeholderLgBase,
        text: 'palette.error.60',
      },
      text: 'palette.tertiary.95',
    },
    focus: {
      ...baseLgProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.tertiary.50',
      label: {
        ...labelLgBase,
        text: 'palette.tertiary.60',
      },
      outlineColor: 'palette.tertiary.50',
      outlineWidth: '1px',
      placeholder: {
        ...placeholderLgBase,
        text: 'palette.transparent',
      },
      text: 'palette.tertiary.95',
    },
    highlightedSearch: 'palette.primary.50:0.5',
    hover: {
      ...baseLgProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.tertiary.50',
      label: {
        ...labelLgBase,
        text: 'palette.tertiary.60',
      },
      placeholder: { ...placeholderLgBase, text: 'palette.tertiary.60' },
      text: 'palette.tertiary.95',
    },
    inactive: {
      ...baseLgProps,
      bg: 'palette.surface.4',
      borderColor: 'palette.tertiary.20',
      label: {
        ...labelLgBase,
        text: 'palette.tertiary.60',
      },
      placeholder: { ...placeholderLgBase, text: 'palette.tertiary.40' },
      text: 'palette.tertiary.95',
    },
    labelBase: labelLgBase,
    loadingText: {
      fontFamily: baseThemeValues.inherit,
      fontSize: '20px',
      fontWeight: 500,
      text: 'palette.primary.99',
    },
    multiTags: {
      tag: {
        bg: 'palette.transparent',
        borderColor: 'palette.neutral.10',
        borderRadius: '12px',
        borderWidth: '1px',
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 500,
        removeButton: 'palette.primary.99',
        text: 'palette.primary.99',
      },
      totalTag: {
        bg: 'palette.primary.20',
        borderColor: 'palette.transparent',
        borderRadius: '12px',
        borderWidth: '0px',
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 500,
        text: 'palette.primary.99',
      },
    },
    openArrowButton: buttonTheme.icon,
    openArrowColor: 'palette.tertiary.40',
    options: {
      active: {
        ...optionsLgBase,
        bg: 'palette.tertiary.20',
        text: 'palette.tertiary.70',
      },
      base: {
        ...optionsLgBase,
        bg: 'palette.transparent',
        text: 'palette.tertiary.95',
      },
      disabled: {
        ...optionsLgBase,
        bg: 'palette.neutral.5',
        text: 'palette.neutral.40',
      },
      hover: {
        ...optionsLgBase,
        bg: 'palette.tertiary.10',
        text: 'palette.tertiary.95',
      },
      optionsBase: optionsLgBase,
      selected: {
        ...optionsLgBase,
        bg: 'palette.transparent',
        text: 'palette.tertiary.95',
      },
    },
    optionsList: {
      bg: 'palette.surface.4',
      borderColor: 'palette.transparent',
      borderRadius: '5px',
      borderWidth: '0px',
      boxShadowColor: 'palette.black:0.5',
      divider: {
        bg: 'palette.transparent',
        height: '1px',
        verticalGap: '0px',
      },
      horizontalGap: '0px',
      verticalGap: '0px',
    },
    placeholderBase: placeholderLgBase,
    scrollBar: {
      ...scrollbarTheme,
      borderWidth: '3px',
      thumb: 'palette.tertiary.20',
      width: '9px',
    },
    warning: {
      label: {
        ...labelLgBase,
        text: 'palette.warning.40',
      },
    },
  },
  sm: {
    baseProps: baseSmProps,
    clearButton: clearButtonTheme,
    disabled: {
      ...baseSmProps,
      bg: 'palette.neutral.10',
      borderColor: 'palette.neutral.10',
      label: {
        ...labelSmBase,
        text: 'palette.neutral.40',
      },
      placeholder: {
        ...placeholderSmBase,
        text: 'palette.neutral.40',
      },
      text: 'palette.neutral.40',
    },
    error: {
      ...baseSmProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.error.60',
      helpText: {
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 400,
        text: 'palette.error.60',
      },
      label: {
        ...labelSmBase,
        text: 'palette.error.60',
      },
      placeholder: { ...placeholderSmBase, text: 'palette.error.60' },
      text: 'palette.tertiary.95',
    },
    focus: {
      ...baseSmProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.tertiary.50',
      label: {
        ...labelSmBase,
        text: 'palette.tertiary.60',
      },
      outlineColor: 'palette.tertiary.50',
      outlineWidth: '1px',
      placeholder: {
        ...placeholderSmBase,
        text: 'palette.transparent',
      },
      text: 'palette.tertiary.95',
    },
    highlightedSearch: 'palette.primary.50:0.5',
    hover: {
      ...baseSmProps,
      bg: 'palette.surface.5',
      borderColor: 'palette.tertiary.50',
      label: {
        ...labelSmBase,
        text: 'palette.tertiary.60',
      },
      placeholder: { ...placeholderSmBase, text: 'palette.tertiary.60' },
      text: 'palette.tertiary.95',
    },
    inactive: {
      ...baseSmProps,
      bg: 'palette.surface.4',
      borderColor: 'palette.tertiary.20',
      label: {
        ...labelSmBase,
        text: 'palette.tertiary.60',
      },
      placeholder: { ...placeholderSmBase, text: 'palette.tertiary.40' },
      text: 'palette.tertiary.95',
    },
    labelBase: labelSmBase,
    loadingText: {
      fontFamily: baseThemeValues.inherit,
      fontSize: '16px',
      fontWeight: 500,
      text: 'palette.primary.99',
    },
    multiTags: {
      tag: {
        bg: 'palette.transparent',
        borderColor: 'palette.tertiary.60',
        borderRadius: '12px',
        borderWidth: '1px',
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 500,
        removeButton: 'palette.tertiary.60',
        text: 'palette.tertiary.60',
      },
      totalTag: {
        bg: 'palette.primary.20',
        borderColor: 'palette.transparent',
        borderRadius: '12px',
        borderWidth: '0px',
        fontFamily: baseThemeValues.inherit,
        fontSize: '12px',
        fontWeight: 500,
        text: 'palette.primary.99',
      },
    },
    openArrowButton: buttonTheme.icon,
    openArrowColor: 'palette.tertiary.40',
    options: {
      active: {
        ...optionsSmBase,
        bg: 'palette.tertiary.20',
        text: 'palette.tertiary.90',
      },
      base: {
        ...optionsSmBase,
        bg: 'palette.transparent',
        text: 'palette.tertiary.95',
      },
      disabled: {
        ...optionsSmBase,
        bg: 'palette.neutral.5',
        text: 'palette.neutral.40',
      },
      hover: {
        ...optionsSmBase,
        bg: 'palette.tertiary.10',
        text: 'palette.tertiary.95',
      },
      optionsBase: optionsSmBase,
      selected: {
        ...optionsSmBase,
        bg: 'palette.transparent',
        text: 'palette.tertiary.95',
      },
    },
    optionsList: {
      bg: 'palette.surface.4',
      borderColor: 'palette.transparent',
      borderRadius: '5px',
      borderWidth: '0px',
      boxShadowColor: 'palette.black:0.5',
      divider: {
        bg: 'palette.transparent',
        height: '1px',
        verticalGap: '0px',
      },
      horizontalGap: '0px',
      verticalGap: '0px',
    },
    placeholderBase: placeholderSmBase,
    scrollBar: {
      ...scrollbarTheme,
      borderWidth: '3px',
      thumb: 'palette.tertiary.20',
      width: '9px',
    },
    warning: {
      label: {
        ...labelSmBase,
        text: 'palette.warning.40',
      },
    },
  },
};
